import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  ClientActivityContainer,
  ClientBusinessDataContainer,
  ClientProfileDetailsContainer,
} from 'pages/Clients/container'
import Breadcrumb from 'components/Clients/Client/Breadcrumb'
import PlatformInfo from 'components/Clients/Client/PlatformInfo'
import Button from 'components/Common/Button'
import ProfileStatus from 'components/Clients/Client/ProfileStatus'
import UserProfileInfo from 'components/Clients/Client/UserProfileInfo'
import ClientDetailHeader from 'components/Clients/Client/ClientDetailHeader'
import { ADVISOR_STATUS, MEETING_STEPPER, ROLES } from 'config'
import Grid from 'components/Common/Grid'
import GrowingContainer from 'components/Common/GrowingContainer'
import { getRoleFromPath, navigateTo, setActiveTabFromUrl } from 'utils/helper'
import TackleAvatarLogo from 'components/Common/TackleAvatarLogo'
import MeetingV2 from 'components/Clients/Client/Meeting_V2/index'
import { RootState } from 'App'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import { ConfirmationModalContainer } from 'pages/Common/container'
import ButtonTabsV2, { Tab } from 'components/Common/ButtonTabsV2'
import AppGoalCenter from 'components/AppGoalCenter/AppGoalCenter.component'
import { useGetBusinessProfileQuery } from '__generated__/api-types-and-hooks'
import AppDashboard from 'components/AppDashboard/AppDashboard.component'
import { AppTools } from 'components/AppTools/AppTools.component'

import { getUserConnectorsAction, resetOAuthUrlAction } from 'store/actions/calendar'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import redirectTo from 'utils/redirectTo'

const Client = ({
  profile,
  getClientProfileAction,
  resetClientsAction,
  openConfirmationModalAction,
  resendUserInviteAction,
}) => {
  const { clientId, tenantId } = useParams()
  const [activeTab, setActiveTab] = useState<string>('')
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false)
  const userRole = getRoleFromPath()
  const { user, openConfirmationModal } = useSelector((state: RootState) => state.user)
  const meetingRecordEnabled = useFeatureFlag(user?.id, 'release-ai-powered-meetings')
  const dataTabDisabled = useFeatureFlag(clientId, 'release-data-tab')
  const isActivityTabEnabled = useFeatureFlag(clientId, 'release-activity-tab')

  const { data: businessData } = useGetBusinessProfileQuery(
    { id: clientId },
    { refetchOnWindowFocus: false }
  )
  useEffect(() => {
    setActiveTab(setActiveTabFromUrl(window.location.href))
    getClientProfileAction({ clientId })
    return () => resetClientsAction()
  }, [clientId, getClientProfileAction, resetClientsAction])

  const changeTabHandler = (step: string) => {
    navigateTo(userRole, `clients/${clientId}/${step}`)
    if (!isFormDataChanged) {
      setActiveTab(step)
    }
  }

  const chatHandler = () => {
    if (window.location.href.includes(ROLES.BSO_ADVISOR)) {
      navigateTo(ROLES.BSO_ADVISOR, `chat/${clientId}`)
    } else {
      navigateTo(ROLES.BSO_ADMIN, `chat/${clientId}`)
    }
  }

  return (
    <GrowingContainer className="!px-0 mt-5">
      <div className="w-full">
        {/* MobileOnly */}
        <div className="xs:block sm:hidden xs:px-6">
          <div>
            {/* profile detail */}
            <ClientDetailHeader />
            <div className="flex flex-col justify-center items-center">
              <UserProfileInfo profile={profile} />
            </div>
            {/* platform start date */}
            <div className="mt-7">
              <PlatformInfo profile={profile} />
            </div>
            {/* Status */}
            <div className="mt-2">
              <ProfileStatus profile={profile} />
            </div>
            {/* Resend Invite */}
            {profile?.status === ADVISOR_STATUS.INVITED && (
              <div className="mt-2">
                <Button
                  variant="text"
                  label="Resend Invitation"
                  onClick={() =>
                    openConfirmationModalAction({
                      openConfirmationModal: true,
                      email: profile?.email,
                      name: profile?.firstName,
                    })
                  }
                />
              </div>
            )}
            <div className="xs:mb-12 sm:mb-0 mt-[24px]">
              <Button
                type="button"
                label="Chat"
                variant="primary"
                onClick={chatHandler}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-4 mt-1">
              <div>
                <AppButton
                  size="sm"
                  label="Book a Meeting"
                  variant="secondary-alt"
                  onClick={() =>
                    redirectTo(`/${userRole}/${tenantId}/calendar?clientId=${clientId}`)
                  }
                />
              </div>
            </div>
          </div>
        </div>
        {/* Laptop & Tablet */}
        <div className="hidden sm:block">
          <Breadcrumb />
        </div>
        <div className="hidden sm:block mt-7">
          <Grid
            className={'px-3 pb-8 sm:px-9 md:px-8 sm:pb-6 items-center space-y-8 mdl:space-y-0'}
          >
            {/* UserLogo  */}
            <div className="col-span-12 mdl:col-span-5 lg:col-span-6">
              <div className="flex items-center space-x-4">
                <div className="">
                  <TackleAvatarLogo
                    className="bg-blue-lighter1x h-[122px] w-[122px]"
                    padding="p-3"
                    logo={businessData?.getBusinessProfile?.logo?.url ?? ''}
                    firstName={profile.firstName}
                    lastName={profile.lastName}
                  />
                </div>
                <div className="px-2">
                  <div className="text-2xl md:text-3xl mdl:text-xl lg:text-2xl xl:text-3xl font-bold">
                    {`${profile?.firstName || ''} ${profile?.lastName || ''}`}
                  </div>
                  <div className="text-xl mdl:text-base lg:text-xl font-bold">
                    {businessData?.getBusinessProfile?.businessName ?? ''}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-12  mdl:col-span-7 lg:col-span-6 pt-6">
              <div className="flex justify-between mdl:justify-end items-center space-x-4 lg:space-x-14 xl:space-x-20">
                {/* Platform start date */}

                <div className="flex items-end space-x-4 lg:space-x-14 xl:space-x-20">
                  <div className="col-span-8">
                    <PlatformInfo profile={profile} />
                  </div>
                  {/* Status */}
                  <div className="col-span-4 ml-5">
                    <div style={{ float: 'right' }}>
                      <ProfileStatus profile={profile} />
                    </div>
                  </div>
                  {/* Resend Invite */}
                  {profile?.status === ADVISOR_STATUS.INVITED && (
                    <div>
                      <Button
                        variant="text"
                        label="Resend Invitation"
                        onClick={() =>
                          openConfirmationModalAction({
                            openConfirmationModal: true,
                            email: profile?.email,
                            name: profile?.firstName,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
                {/* ChatButton */}
                <div>
                  <Button
                    type="button"
                    label="Chat"
                    variant="primary"
                    onClick={chatHandler}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
            <div className="mt-1 col-span-12 flex items-center gap-4 p-2 mdl:pl-[140px]">
              <div>
                <AppButton
                  size="sm"
                  label="Book a Meeting"
                  variant="secondary-alt"
                  onClick={() =>
                    redirectTo(`/${userRole}/${tenantId}/calendar?clientId=${clientId}`)
                  }
                />
              </div>
            </div>
          </Grid>
        </div>

        <ButtonTabsV2
          activeTab={activeTab}
          onChange={changeTabHandler}
          size="sm"
          classes="bg-white mt-14"
        >
          <Tab
            component={
              <ClientProfileDetailsContainer
                setActiveTab={setActiveTab}
                isFormDataChanged={isFormDataChanged}
                setIsFormDataChanged={setIsFormDataChanged}
              />
            }
            title="Business Details"
            name="details"
          />
          <Tab title="Command Center" name="commandCenter" component={<AppDashboard />} />
          <Tab
            title="Insights"
            name="insights"
            component={<ClientBusinessDataContainer />}
            isEnabled={!dataTabDisabled}
          />
          <Tab
            title="Goal Center"
            name="goals"
            component={
              <AppGoalCenter businessName={businessData?.getBusinessProfile?.businessName ?? ''} />
            }
          />
          <Tab
            title="Tech Tools"
            name="techTools"
            component={
              <>
                <h1 className="font-medium font-inter text-[24px] leading-9">
                  🏈 {businessData?.getBusinessProfile?.businessName ?? ''}'s Tech Tools
                </h1>
                <AppTools readonly={true} />
              </>
            }
          />
          <Tab
            title="Meeting Records"
            name="meetingRecordV2"
            component={
              <MeetingV2
                upComingMeeting={[]}
                previousMeeting={[1, 1]}
                user={profile}
                oAuthUrl={''}
                getUserConnectorsAction={getUserConnectorsAction}
                resetOAuthUrlAction={resetOAuthUrlAction}
                meetingStepper={MEETING_STEPPER.ADVISOR}
              />
            }
            isEnabled={meetingRecordEnabled}
          />
          <Tab
            title="Activity"
            name="activity"
            component={<ClientActivityContainer />}
            isEnabled={isActivityTabEnabled}
          />
        </ButtonTabsV2>
        {openConfirmationModal && (
          <ConfirmationModalContainer
            title="Resend user invitation"
            onConfirm={() =>
              resendUserInviteAction({
                name: `${profile?.firstName} ${profile?.lastName}`,
                email: profile?.email,
                userId: profile?.id,
              })
            }
            description={`Resending the invitation will send an email to ${profile?.email}`}
            successDescription={`The invitation has been successfully resent to  ${profile?.email}`}
            onClose={() => openConfirmationModalAction({ openConfirmationModal: false })}
          />
        )}
      </div>
    </GrowingContainer>
  )
}

export default Client
